import { useState, useCallback } from 'react';
import { API_BASE } from '../../../config/constants';

export function useWatch(userId?: string | number | null) {
    const [watchList, setWatchList] = useState<Array<any>>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const loadWatchList = useCallback(async () => {
        if (userId === null || userId === undefined || String(userId).trim() === '') {
            console.debug('useWatch: loadWatchList skipped - missing userId', { userId });
            setWatchList([]);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/api/objects/get-user-watchlist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId }),
            });

            const data = await response.json();
            if (data && Array.isArray(data.data)) {
                setWatchList(data.data);
            } else if (Array.isArray(data)) {
                setWatchList(data);
            } else {
                setWatchList([]);
            }
        } catch (err) {
            console.error('useWatch: Failed to load watch list', err);
            setWatchList([]);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    // NOTE: do not auto-load on mount — callers should explicitly call `loadWatchList`

    const createWatch = useCallback(
        async (targetUserId: string | number, objectId: string | number): Promise<boolean> => {
            try {
                console.debug('useWatch: createWatch request', { targetUserId, objectId });
                const response = await fetch(`${API_BASE}/api/objects/create-watch`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user_id: targetUserId, object_id: objectId }),
                });

                const result = await response.json().catch(() => ({}));
                console.debug('useWatch: createWatch response', { status: response.status, ok: response.ok, result });
                if (!response.ok || !result?.success) return false;
                await loadWatchList();
                return true;
            } catch (err) {
                console.error('useWatch: createWatch error', err);
                return false;
            }
        },
        [loadWatchList]
    );

    const removeWatch = useCallback(
        async (targetUserId: string | number, objectId: string | number): Promise<boolean> => {
            try {
                console.debug('useWatch: removeWatch request', { targetUserId, objectId });
                const response = await fetch(`${API_BASE}/api/objects/remove-watch`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user_id: targetUserId, object_id: objectId }),
                });

                const result = await response.json().catch(() => ({}));
                console.debug('useWatch: removeWatch response', { status: response.status, ok: response.ok, result });
                if (!response.ok || !result?.success) return false;
                await loadWatchList();
                return true;
            } catch (err) {
                console.error('useWatch: removeWatch error', err);
                return false;
            }
        },
        [loadWatchList]
    );

    return { watchList, loading, loadWatchList, createWatch, removeWatch };
}

export default useWatch;
